import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { getApiUrl } from '../utils/apiUrl';

const AdminWaitlistPage = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [waitlist, setWaitlist] = useState([]);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedEntries, setSelectedEntries] = useState(new Set());
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showManualInviteModal, setShowManualInviteModal] = useState(false);
  const [manualInvite, setManualInvite] = useState({ email: '', name: '', notes: '' });
  const [inviteResult, setInviteResult] = useState(null);
  const [notes, setNotes] = useState('');
  const [sendEmail, setSendEmail] = useState(true);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      return;
    }
    loadData();
  }, [isAdmin, statusFilter]);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Load stats
      const statsRes = await axios.get(`${getApiUrl()}/waitlist/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(statsRes.data);

      // Load waitlist entries
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (search) params.append('search', search);

      const listRes = await axios.get(`${getApiUrl()}/waitlist?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWaitlist(listRes.data.waitlist || []);
    } catch (error) {
      console.error('Error loading waitlist:', error);
      alert('Failed to load waitlist data');
    }
    setLoading(false);
  };

  const handleApprove = async (entry) => {
    setSelectedEntry(entry);
    setShowApproveModal(true);
  };

  const confirmApprove = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${getApiUrl()}/waitlist/${selectedEntry.id}/approve`,
        { send_email: sendEmail, notes },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert(`✅ Approved! Invite code: ${response.data.invite_code}\nEmail sent: ${response.data.email_sent}`);
      setShowApproveModal(false);
      setNotes('');
      loadData();
    } catch (error) {
      console.error('Error approving:', error);
      alert('Failed to approve entry');
    }
  };

  const handleReject = async (entry) => {
    setSelectedEntry(entry);
    setShowRejectModal(true);
  };

  const confirmReject = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${getApiUrl()}/waitlist/${selectedEntry.id}/reject`,
        { notes },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert('Entry rejected');
      setShowRejectModal(false);
      setNotes('');
      loadData();
    } catch (error) {
      console.error('Error rejecting:', error);
      alert('Failed to reject entry');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this entry permanently?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${getApiUrl()}/waitlist/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('Entry deleted');
      loadData();
    } catch (error) {
      console.error('Error deleting:', error);
      alert('Failed to delete entry');
    }
  };

  const handleBulkApprove = async () => {
    if (selectedEntries.size === 0) {
      alert('No entries selected');
      return;
    }

    if (!window.confirm(`Approve ${selectedEntries.size} entries and send invite emails?`)) return;

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${getApiUrl()}/waitlist/bulk-approve`,
        { ids: Array.from(selectedEntries), send_emails: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const successful = response.data.results.filter(r => r.success).length;
      alert(`✅ Successfully approved ${successful} out of ${selectedEntries.size} entries`);
      setSelectedEntries(new Set());
      loadData();
    } catch (error) {
      console.error('Error bulk approving:', error);
      alert('Failed to bulk approve');
    }
  };

  const toggleSelection = (id) => {
    const newSelected = new Set(selectedEntries);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedEntries(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedEntries.size === waitlist.length) {
      setSelectedEntries(new Set());
    } else {
      setSelectedEntries(new Set(waitlist.map(e => e.id)));
    }
  };

  const handleManualInvite = async () => {
    if (!manualInvite.email) {
      alert('Email is required');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${getApiUrl()}/waitlist/manual-invite`,
        {
          email: manualInvite.email,
          name: manualInvite.name,
          send_email: sendEmail,
          notes: manualInvite.notes || 'Manual alpha invite'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setInviteResult(response.data);
      
      // Don't close modal immediately so admin can copy invite code
      if (!sendEmail) {
        alert(`✅ Invite created!\n\nInvite Code: ${response.data.invite_code}\n\nPlease share this code with ${manualInvite.email}`);
      } else {
        alert(`✅ Invite sent to ${manualInvite.email}!\n\nInvite Code: ${response.data.invite_code}\nEmail sent: ${response.data.email_sent}`);
      }
      
      // Refresh data
      loadData();
    } catch (error) {
      console.error('Error sending manual invite:', error);
      const errorMsg = error.response?.data?.error || error.message;
      alert(`Failed to send invite: ${errorMsg}`);
    }
  };

  if (!isAdmin) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h2>Access Denied</h2>
        <p>You don't have permission to access this page.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{
        padding: '2rem',
        paddingTop: '5rem',
        textAlign: 'center',
        background: 'linear-gradient(135deg, #141517 0%, #1f2023 100%)',
        minHeight: '100vh',
        color: '#9ca3af'
      }}>
        Loading waitlist...
      </div>
    );
  }

  return (
    <div style={{
      padding: '2rem',
      paddingTop: '5rem',
      maxWidth: '1400px',
      margin: '0 auto',
      background: 'linear-gradient(135deg, #141517 0%, #1f2023 100%)',
      minHeight: '100vh'
    }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{
          fontSize: '2.5rem',
          background: 'linear-gradient(135deg, #9c3f45 0%, #ea580c 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          marginBottom: '0.5rem'
        }}>
          📋 Waitlist Management
        </h1>
        <p style={{ color: '#9ca3af', fontSize: '1.1rem' }}>
          Manage beta access requests and invite codes
        </p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem',
          marginBottom: '2rem'
        }}>
          <div style={{
            background: 'rgba(234, 179, 8, 0.1)',
            border: '1px solid rgba(234, 179, 8, 0.3)',
            borderRadius: '12px',
            padding: '1.5rem'
          }}>
            <div style={{ color: '#9ca3af', fontSize: '0.9rem', marginBottom: '0.5rem' }}>Pending</div>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#eab308' }}>{stats.pending_count}</div>
          </div>

          <div style={{
            background: 'rgba(34, 197, 94, 0.1)',
            border: '1px solid rgba(34, 197, 94, 0.3)',
            borderRadius: '12px',
            padding: '1.5rem'
          }}>
            <div style={{ color: '#9ca3af', fontSize: '0.9rem', marginBottom: '0.5rem' }}>Invited</div>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#22c55e' }}>{stats.invited_count}</div>
          </div>

          <div style={{
            background: 'rgba(59, 130, 246, 0.1)',
            border: '1px solid rgba(59, 130, 246, 0.3)',
            borderRadius: '12px',
            padding: '1.5rem'
          }}>
            <div style={{ color: '#9ca3af', fontSize: '0.9rem', marginBottom: '0.5rem' }}>Registered</div>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#3b82f6' }}>{stats.registered_count}</div>
          </div>

          <div style={{
            background: 'rgba(220, 38, 38, 0.1)',
            border: '1px solid rgba(220, 38, 38, 0.3)',
            borderRadius: '12px',
            padding: '1.5rem'
          }}>
            <div style={{ color: '#9ca3af', fontSize: '0.9rem', marginBottom: '0.5rem' }}>This Week</div>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#9c3f45' }}>{stats.this_week}</div>
          </div>
        </div>
      )}

      {/* Manual Invite Button */}
      <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'flex-end' }}>
        <button
          onClick={() => {
            setShowManualInviteModal(true);
            setManualInvite({ email: '', name: '', notes: '' });
            setInviteResult(null);
            setSendEmail(true);
          }}
          style={{
            padding: '1rem 2rem',
            background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
            border: 'none',
            borderRadius: '8px',
            color: 'white',
            fontWeight: 'bold',
            cursor: 'pointer',
            fontSize: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          ✉️ Manual Invite
        </button>
      </div>

      {/* Filters and Actions */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.05)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        padding: '1.5rem',
        borderRadius: '12px',
        marginBottom: '2rem'
      }}>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Search by email or name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && loadData()}
            style={{
              flex: 1,
              minWidth: '200px',
              padding: '0.75rem',
              background: 'rgba(0, 0, 0, 0.3)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '8px',
              color: 'white'
            }}
          />

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              padding: '0.75rem',
              background: 'rgba(0, 0, 0, 0.3)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '8px',
              color: 'white'
            }}
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="invited">Invited</option>
            <option value="registered">Registered</option>
            <option value="rejected">Rejected</option>
          </select>

          <button
            onClick={loadData}
            style={{
              padding: '0.75rem 1.5rem',
              background: 'linear-gradient(135deg, #ea580c 0%, #c2410c 100%)',
              border: 'none',
              borderRadius: '8px',
              color: 'white',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            🔍 Search
          </button>

          {selectedEntries.size > 0 && (
            <button
              onClick={handleBulkApprove}
              style={{
                padding: '0.75rem 1.5rem',
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              ✅ Approve Selected ({selectedEntries.size})
            </button>
          )}
        </div>
      </div>

      {/* Waitlist Table */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.05)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '12px',
        overflow: 'hidden'
      }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'rgba(0, 0, 0, 0.3)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                <th style={{ padding: '1rem', textAlign: 'left' }}>
                  <input
                    type="checkbox"
                    checked={selectedEntries.size === waitlist.length && waitlist.length > 0}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th style={{ padding: '1rem', textAlign: 'left', color: '#9ca3af' }}>Email</th>
                <th style={{ padding: '1rem', textAlign: 'left', color: '#9ca3af' }}>Name</th>
                <th style={{ padding: '1rem', textAlign: 'left', color: '#9ca3af' }}>Status</th>
                <th style={{ padding: '1rem', textAlign: 'left', color: '#9ca3af' }}>Invite Code</th>
                <th style={{ padding: '1rem', textAlign: 'left', color: '#9ca3af' }}>Date</th>
                <th style={{ padding: '1rem', textAlign: 'left', color: '#9ca3af' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {waitlist.map((entry) => (
                <tr
                  key={entry.id}
                  style={{
                    borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                    background: selectedEntries.has(entry.id) ? 'rgba(220, 38, 38, 0.1)' : 'transparent'
                  }}
                >
                  <td style={{ padding: '1rem' }}>
                    <input
                      type="checkbox"
                      checked={selectedEntries.has(entry.id)}
                      onChange={() => toggleSelection(entry.id)}
                    />
                  </td>
                  <td style={{ padding: '1rem', color: '#9ca3af' }}>{entry.email}</td>
                  <td style={{ padding: '1rem', color: '#9ca3af' }}>{entry.name || '-'}</td>
                  <td style={{ padding: '1rem' }}>
                    <span style={{
                      padding: '0.25rem 0.75rem',
                      borderRadius: '12px',
                      fontSize: '0.85rem',
                      fontWeight: 'bold',
                      background: entry.status === 'pending' ? 'rgba(234, 179, 8, 0.2)' :
                                 entry.status === 'invited' ? 'rgba(34, 197, 94, 0.2)' :
                                 entry.status === 'registered' ? 'rgba(59, 130, 246, 0.2)' :
                                 'rgba(220, 38, 38, 0.2)',
                      color: entry.status === 'pending' ? '#eab308' :
                            entry.status === 'invited' ? '#22c55e' :
                            entry.status === 'registered' ? '#3b82f6' :
                            '#9c3f45'
                    }}>
                      {entry.status}
                    </span>
                  </td>
                  <td style={{ padding: '1rem', color: '#9ca3af', fontSize: '0.9rem', fontFamily: 'monospace' }}>
                    {entry.invite_code ? (
                      <span style={{
                        background: entry.code_used_at ? 'rgba(156, 163, 175, 0.1)' : 'rgba(34, 197, 94, 0.1)',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px',
                        border: `1px solid ${entry.code_used_at ? '#6b7280' : '#22c55e'}`
                      }}>
                        {entry.invite_code}
                        {entry.code_used_at && <span style={{ color: '#6b7280', fontSize: '0.8rem' }}> (used)</span>}
                      </span>
                    ) : (
                      <span style={{ color: '#6b7280' }}>-</span>
                    )}
                  </td>
                  <td style={{ padding: '1rem', color: '#9ca3af', fontSize: '0.9rem' }}>
                    {new Date(entry.created_at).toLocaleDateString()}
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      {entry.status === 'pending' && (
                        <>
                          <button
                            onClick={() => handleApprove(entry)}
                            style={{
                              padding: '0.5rem 1rem',
                              background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                              border: 'none',
                              borderRadius: '6px',
                              color: 'white',
                              fontSize: '0.85rem',
                              cursor: 'pointer'
                            }}
                          >
                            ✅ Approve
                          </button>
                          <button
                            onClick={() => handleReject(entry)}
                            style={{
                              padding: '0.5rem 1rem',
                              background: 'linear-gradient(135deg, #9c3f45 0%, #b91c1c 100%)',
                              border: 'none',
                              borderRadius: '6px',
                              color: 'white',
                              fontSize: '0.85rem',
                              cursor: 'pointer'
                            }}
                          >
                            ❌ Reject
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => handleDelete(entry.id)}
                        style={{
                          padding: '0.5rem 1rem',
                          background: 'rgba(156, 163, 175, 0.2)',
                          border: '1px solid rgba(156, 163, 175, 0.3)',
                          borderRadius: '6px',
                          color: '#9ca3af',
                          fontSize: '0.85rem',
                          cursor: 'pointer'
                        }}
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {waitlist.length === 0 && (
          <div style={{ padding: '3rem', textAlign: 'center', color: '#9ca3af' }}>
            No waitlist entries found
          </div>
        )}
      </div>

      {/* Approve Modal */}
      {showApproveModal && selectedEntry && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #141517 0%, #1f2023 100%)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h2 style={{ color: '#22c55e', marginBottom: '1rem' }}>✅ Approve Entry</h2>
            <p style={{ color: '#9ca3af', marginBottom: '1.5rem' }}>
              Approve <strong>{selectedEntry.email}</strong> and send invite code?
            </p>

            <label style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem', color: '#9ca3af' }}>
              <input
                type="checkbox"
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
                style={{ marginRight: '0.5rem' }}
              />
              Send welcome email with invite code
            </label>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', color: '#9ca3af', marginBottom: '0.5rem' }}>
                Notes (optional):
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add notes..."
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: 'rgba(0, 0, 0, 0.3)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '8px',
                  color: 'white',
                  minHeight: '80px'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowApproveModal(false)}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: 'rgba(156, 163, 175, 0.2)',
                  border: '1px solid rgba(156, 163, 175, 0.3)',
                  borderRadius: '8px',
                  color: '#9ca3af',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmApprove}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                Approve & Send Invite
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && selectedEntry && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #141517 0%, #1f2023 100%)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h2 style={{ color: '#9c3f45', marginBottom: '1rem' }}>❌ Reject Entry</h2>
            <p style={{ color: '#9ca3af', marginBottom: '1.5rem' }}>
              Reject <strong>{selectedEntry.email}</strong>?
            </p>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', color: '#9ca3af', marginBottom: '0.5rem' }}>
                Reason (optional):
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add reason..."
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: 'rgba(0, 0, 0, 0.3)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '8px',
                  color: 'white',
                  minHeight: '80px'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowRejectModal(false)}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: 'rgba(156, 163, 175, 0.2)',
                  border: '1px solid rgba(156, 163, 175, 0.3)',
                  borderRadius: '8px',
                  color: '#9ca3af',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmReject}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: 'linear-gradient(135deg, #9c3f45 0%, #b91c1c 100%)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                Reject Entry
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manual Invite Modal */}
      {showManualInviteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #141517 0%, #1f2023 100%)',
            border: '1px solid rgba(139, 92, 246, 0.3)',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h2 style={{ color: '#8b5cf6', marginBottom: '1rem' }}>
              ✉️ Send Manual Invite
            </h2>
            <p style={{ color: '#9ca3af', marginBottom: '1.5rem', fontSize: '0.95rem' }}>
              Directly invite someone for alpha testing. They'll receive an invite code to register.
            </p>

            {inviteResult ? (
              <div>
                <div style={{
                  background: 'rgba(34, 197, 94, 0.1)',
                  border: '1px solid rgba(34, 197, 94, 0.3)',
                  borderRadius: '8px',
                  padding: '1.5rem',
                  marginBottom: '1.5rem'
                }}>
                  <p style={{ color: '#22c55e', fontWeight: 'bold', marginBottom: '1rem' }}>
                    ✅ Invite Sent Successfully!
                  </p>
                  <div style={{ color: '#9ca3af', marginBottom: '0.5rem' }}>
                    <strong>Email:</strong> {manualInvite.email}
                  </div>
                  <div style={{ color: '#9ca3af', marginBottom: '1rem' }}>
                    <strong>Email Sent:</strong> {inviteResult.email_sent ? 'Yes ✅' : 'No (email not configured)'}
                  </div>
                  
                  <div style={{
                    marginTop: '1rem',
                    padding: '1rem',
                    background: 'rgba(139, 92, 246, 0.1)',
                    border: '1px solid rgba(139, 92, 246, 0.3)',
                    borderRadius: '6px'
                  }}>
                    <p style={{ color: '#8b5cf6', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                      Invite Code:
                    </p>
                    <p style={{ 
                      color: 'white',
                      fontFamily: 'monospace',
                      fontSize: '1.2rem',
                      background: 'rgba(0, 0, 0, 0.3)',
                      padding: '0.75rem',
                      borderRadius: '4px',
                      userSelect: 'all',
                      wordBreak: 'break-all'
                    }}>
                      {inviteResult.invite_code}
                    </p>
                    <p style={{ color: '#9ca3af', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                      {inviteResult.email_sent 
                        ? 'Code was sent via email to the user.'
                        : 'Please share this code with the user manually.'}
                    </p>
                  </div>
                </div>

                <button
                  onClick={() => {
                    setShowManualInviteModal(false);
                    setManualInvite({ email: '', name: '', notes: '' });
                    setInviteResult(null);
                  }}
                  style={{
                    padding: '0.75rem',
                    width: '100%',
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                    border: 'none',
                    borderRadius: '6px',
                    color: 'white',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  Close
                </button>
              </div>
            ) : (
              <div>
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', color: '#9ca3af', marginBottom: '0.5rem' }}>
                    Email Address *
                  </label>
                  <input
                    type="email"
                    value={manualInvite.email}
                    onChange={(e) => setManualInvite({ ...manualInvite, email: e.target.value })}
                    placeholder="user@example.com"
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: 'rgba(0, 0, 0, 0.3)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '8px',
                      color: 'white'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', color: '#9ca3af', marginBottom: '0.5rem' }}>
                    Name (optional)
                  </label>
                  <input
                    type="text"
                    value={manualInvite.name}
                    onChange={(e) => setManualInvite({ ...manualInvite, name: e.target.value })}
                    placeholder="John Doe"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: 'rgba(0, 0, 0, 0.3)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '8px',
                      color: 'white'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', color: '#9ca3af', marginBottom: '0.5rem' }}>
                    Notes (optional)
                  </label>
                  <textarea
                    value={manualInvite.notes}
                    onChange={(e) => setManualInvite({ ...manualInvite, notes: e.target.value })}
                    placeholder="Reason for invite, special instructions, etc."
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: 'rgba(0, 0, 0, 0.3)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '8px',
                      color: 'white',
                      minHeight: '80px',
                      resize: 'vertical'
                    }}
                  />
                </div>

                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  marginBottom: '1.5rem', 
                  color: '#9ca3af',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    checked={sendEmail}
                    onChange={(e) => setSendEmail(e.target.checked)}
                    style={{ marginRight: '0.5rem' }}
                  />
                  Send welcome email with invite code
                </label>

                <div style={{ display: 'flex', gap: '1rem' }}>
                  <button
                    onClick={() => {
                      setShowManualInviteModal(false);
                      setManualInvite({ email: '', name: '', notes: '' });
                      setInviteResult(null);
                    }}
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      background: 'rgba(156, 163, 175, 0.2)',
                      border: '1px solid rgba(156, 163, 175, 0.3)',
                      borderRadius: '8px',
                      color: '#9ca3af',
                      cursor: 'pointer',
                      fontWeight: 'bold'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleManualInvite}
                    disabled={!manualInvite.email}
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      background: !manualInvite.email
                        ? 'rgba(255, 255, 255, 0.05)'
                        : 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                      border: 'none',
                      borderRadius: '8px',
                      color: !manualInvite.email ? '#6b7280' : 'white',
                      cursor: !manualInvite.email ? 'not-allowed' : 'pointer',
                      fontWeight: 'bold'
                    }}
                  >
                    Send Invite
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminWaitlistPage;
